import {inject, Pipe, PipeTransform} from '@angular/core';
import {CurrencyService} from "../../services/currency.service";
import {StateService} from "../../services/state/state.service";
import {CurrencyDto} from "../../repositories/dto/currency.dto";
import {LoggingService} from "../../services/logger/logging.service";
import {isDefined} from "../utils/epic.utils";
import {CalcService} from "../../services/calc.service";

export interface AccAmountPipe {

  getCurrencyCode(): string;

  getPreferCurrencyCode(): string | undefined;

  getFractionalDigits(): number;
}

@Pipe({name: 'accAmount', pure: true, standalone: true})
export class AmountPipe implements PipeTransform {

  private readonly currencyService: CurrencyService = inject(CurrencyService);
  private readonly stateService: StateService = inject(StateService);
  private readonly log: LoggingService = inject(LoggingService);

  private readonly defaultDigits: number = CalcService.MIN_SCALE;
  private defaultCurrency: CurrencyDto | undefined;

  constructor() {
    this.stateService.getOrgDistinct().subscribe((o) => this.defaultCurrency = o.defaultCurrency);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  transform(value: number | undefined, currencyData?: any): string {
    if (isDefined(value)) {
      let currencyCode, digits;
      // Document/Object with the implementation of AccAmountPipe was given
      if (isDefined(currencyData) && this.isAccAmountPipe(currencyData)) {
        const preferCurrencyCode = currencyData.getPreferCurrencyCode();
        currencyCode = isDefined(preferCurrencyCode) ? preferCurrencyCode : currencyData.getCurrencyCode();
        digits = currencyData.getFractionalDigits();
      }
      // CurrencyCode was given directly as a CurrencyDto
      else if (isDefined(currencyData) && currencyData instanceof CurrencyDto) {
        currencyCode = currencyData.code;
        digits = this.defaultDigits;
      }
      // CurrencyCode was given directly as a string
      else if (typeof currencyData === 'string' && currencyData.length === 3) {
        currencyCode = currencyData;
        digits = this.defaultDigits;
      }
      // Currency code should be hidden
      else if (typeof currencyData === 'string' && currencyData === CurrencyDto.NONE.code) {
        currencyCode = CurrencyDto.NONE.code;
        digits = this.defaultDigits;
      }
      // Fallback to default currency and digits
      else if (this.defaultCurrency) {
        currencyCode = this.defaultCurrency.code;
        digits = this.defaultDigits;
        this.log.warn("CurrencyCode not found for AmountPipe, fallback to Org-Default '{}'", [this.defaultCurrency.code]);
      } else {
        throw new Error('AmountPipe: "' + currencyData?.constructor.name + '" not supported');
      }

      const amount: string | undefined = this.currencyService.formatAmount(value, currencyCode, digits);
      if (isDefined(amount)) {
        return amount;
      }
    }
    return '';
  }

  private isAccAmountPipe(obj: any): obj is AccAmountPipe {
    return typeof obj.getCurrencyCode === "function"
      && typeof obj.getPreferCurrencyCode === "function"
      && typeof obj.getFractionalDigits === "function";
  }
}
